import ContentConfig, { MaybeContentConfig } from '../config/ContentConfig';
import EventEmitter from './EventEmitter';
import LocalState from '../state/LocalState';
import InterceptorStack from '../interceptor/InterceptorStack';
import NodeImpl from '../webrtc/agent/Node';
import Reporter from './Reporter/Reporter';
import SDKConfig from './SDKConfig';
import autobind from '../autobind';
import createInterceptorStack from '../interceptor/createInterceptorStack';
import getKollectiveHeaders from './utils/getKollectiveHeaders';
import listenToRequestIngest from '../interceptor/listenToRequestIngest';
import logger from '../log';
import { ApiLogger } from '../log/Logger';
import { DeliveryMode, SourceType } from '../common/constants';
import {
  ErrorMessages,
  Handler,
  OidFunction
} from '@kollective-frontend/ksdk-api';
import { EventData, IPlugin, KollectiveContentItem } from './interfaces';
import { Events, InternalEvents } from '../events';
import { NOT_PEERABLE } from './constants';
import { Node, PeerData } from '../webrtc/agent/interfaces';
import {
  OptionalKollectiveSources,
  PlaybackModule
} from '../playback/interfaces';
import { dispatchPlayerEvent } from '../events/dispatch';
import BaseInteraction from '../player/BaseInteraction';
import { TechApi } from '../bootloader/tech';
import configureReportLogging from './configureReportLogging';
import { NonStdPlayerEvents } from './Reporter/constants';
import { LoadOpts } from './Api';
import { setUserAgentHeaders } from '../util/getUserAgentHeaders';

const log = logger.getLogger(__filename);
const config = ContentConfig.getInstance();
const sessionLogger = logger.getSessionLogger('events');

type Rsu = Record<string, unknown>;

export interface LoadOptions extends LoadOpts {
  contentTitle?: string;
  contentUrl?: string;
  legacySourceTypeFallback: SourceType;
  // The master playlist string that will be passed to the constructor for the playerInteraction.
  masterPlaylist?: string;
  masterPlaylistUrl?: string;
  sourceType?: SourceType;
  token?: string;
  videoElement?: HTMLVideoElement;
}

export class ErrorPlaybackModule implements PlaybackModule {
  get mode(): DeliveryMode {
    log.info('No playback module set up: mode');
    return DeliveryMode.NONE;
  }

  init(): Promise<void> {
    log.info('No playback module set up: init()');
    return Promise.resolve();
  }

  startDelivery(): Promise<OptionalKollectiveSources> {
    log.info('No playback module set up: startDelivery()');
    return Promise.resolve(undefined);
  }

  stop(): void {
    log.info('No playback module set up: stop()');
  }
}

export const transformItemForLogEvent = (event: Rsu, item: Rsu): void => {
  const { data: dataBlock } = event;
  const { data, level, type } = dataBlock as Rsu;
  const { ts, msg } = data as Record<string, unknown>;
  item.tag = 'level';
  item.msg = { level, ts, msg, type };
};

export default abstract class BasePlugin implements IPlugin {
  abstract getVideoElement(): unknown;
  abstract load(options: LoadOptions): Promise<BasePlugin>;
  abstract fallbackToCDN(error?: unknown): void;
  abstract switchToCDN(error?: unknown): void;
  abstract playKollective(sources: OptionalKollectiveSources): void;

  tech: TechApi;
  contentSources: KollectiveContentItem[];
  emitter: EventEmitter;
  hasLoadStarted: boolean;
  integratorToken?: string;
  log: ApiLogger;
  playbackModule: PlaybackModule;
  player: unknown;
  playerInteraction?: BaseInteraction<unknown>;
  reporter: Reporter;
  sdkConfig: SDKConfig;
  shouldIntercept: boolean;
  state: LocalState;
  stack!: InterceptorStack;
  createObjectId?: OidFunction;

  constructor(
    sdkConfig: SDKConfig,
    player: unknown,
    tech: TechApi,
    emitter = EventEmitter.getInstance()
  ) {
    autobind(this);
    this.emitter = emitter;
    this.state = new LocalState({ play: { sdkConfig } } as LocalState);
    this.sdkConfig = sdkConfig;
    config.app = sdkConfig.integrationName();
    this.contentSources = [];
    this.hasLoadStarted = false;
    this.player = player;
    this.playbackModule = new ErrorPlaybackModule();
    this.tech = tech;

    // start shouldIntercept false, because the integrator may have already set
    // the src, and we may not want to intercept that. Also, we don't want
    // intercept until the `load` method is called and we have a tenant id and
    // content id.
    this.shouldIntercept = false;

    this.log = log.asApiLogger();
    const activeDataSources = {
      currentPeerConnections: () => this.stack.currentPeerConnections()
    };
    this.reporter = new Reporter(
      activeDataSources,
      this.state,
      config.app,
      this.emitter
    );

    const { visibilitychange, pagehide, beforeunload } = NonStdPlayerEvents;
    document.addEventListener(visibilitychange.name, () => {
      if (document.visibilityState === 'hidden') {
        dispatchPlayerEvent({ e: visibilitychange.code });
      }
    });

    [pagehide, beforeunload].forEach(nonStandardEvent => {
      const { name, code } = nonStandardEvent;
      window.addEventListener(name, (evt: unknown) => {
        if (this.isEligibleStopEvent(evt as Record<string, unknown>)) {
          try {
            dispatchPlayerEvent({ e: code });
            this.stop(true);
          } catch (e) {
            log.warn('Cannot shutdown kollective sdk instance', this, e);
          }
        }
      });
    });

    setUserAgentHeaders();

    // eslint-disable-next-line no-console
    console.log(`Ksdk  v ${sdkConfig.version} initialized`);

    listenToRequestIngest(this.emitter, () => {
      const agent = this.playbackModule.agent;
      const { contentId, tenantId } = this.state.play;
      const node: Node = agent
        ? (agent.getNode() as Node)
        : ({
            ...NodeImpl.create(tenantId as string, contentId as string),
            clusterId: NOT_PEERABLE
          } as Node);
      const appName = ContentConfig.getInstance().app as string;
      const cacheHandler = (objectId: string, data: unknown) => {
        if (agent) {
          agent.putObject(objectId, data as PeerData);
        }
      };
      return {
        items: this.contentSources,
        node,
        appName,
        peerEnabled: (agent && agent.initialized) || false,
        playType: this.state.play.playType,
        cacheHandler
      };
    });

    // Set up events to be documented.
    [
      InternalEvents.ATTACHMENT_REQUEST,
      InternalEvents.CONNECTION_RECORD,
      InternalEvents.CONTENT_NOTIFICATION,
      InternalEvents.DETACH,
      InternalEvents.LOG,
      InternalEvents.NODE_STATE,
      InternalEvents.PEER_NOTIFICATION,
      InternalEvents.PEER_REQUEST,
      InternalEvents.TRANSFER_RECORD
    ].forEach(type => {
      this.emitter.addEventListener(type, event => {
        const item = { section: type, msg: { type, ...event } };
        if (type === InternalEvents.LOG) {
          transformItemForLogEvent(event, item);
        }
        sessionLogger.msg(item);
      });
    });
  }

  isEligibleStopEvent(evt: Record<string, unknown>): boolean {
    switch (evt.type) {
      case 'beforeunload':
        return true;
      case 'pagehide':
        return !evt.persisted;
      default:
        return false;
    }
  }

  createInterceptorStack(): void {
    // These should be assigned by the time that this method is called.
    const { contentId, tenantId } = this.state.play as {
      tenantId: string;
      contentId: string;
    };
    // Create stack now that we are handling adding/removing interceptor events.
    this.stack = createInterceptorStack({
      items: this.contentSources,
      sourceType: this.state.play.sourceType as SourceType,
      playType: this.state.play.playType,
      config: this.getConfig(),
      sdkVer: this.sdkConfig.version as string,
      sessionToken: this.state.reporter.sessionToken as string,
      tenantId,
      contentId,
      changeHandler: { state: this.state.reporter, emitter: this.emitter },
      createObjectId: this.createObjectId
    });
  }

  mergeConfig(inConfig: MaybeContentConfig): void {
    config.merge(inConfig, { from: 'mergeConfig' });
    configureReportLogging(true);
  }

  isEcdnUsed(): boolean {
    const { isKollectiveDisabled: disabled } = this.state.play;
    return typeof disabled === 'undefined' ? true : !disabled;
  }

  // Set up common functionality.
  stop(useBeacon?: boolean): void {
    this.shouldIntercept = false;
    if (this.state.play.stopped) {
      log.warn('Skipping, stop as it has already been called');
      return;
    }
    this.state.play.stopped = true;
    log.warn('Stopping Kollective SDK plugin');
    this.playbackModule.stop();
    this.reporter.cleanup(useBeacon);
    if (this.playerInteraction) {
      this.playerInteraction.cleanup();
    }
    const events = this.emitter.listEvents();
    log.debug('Events: ', events);
    this.emitter.clearEvents();
  }

  getConfig(): ContentConfig {
    return ContentConfig.getCopy();
  }

  getContext(): string {
    return this.state.play.sdkConfig.integrationName();
  }

  addEventListener = (e: Events, h: Handler<EventData>): void => {
    this.emitter.addEventListener(e, h);
  };

  removeEventListener = (e: Events, h: Handler<EventData>): void => {
    this.emitter.removeEventListener(e, h);
  };

  /**
   * @returns string -- the first available token, in the following order:
   * 1. the session token
   * 2. the content token
   * 3. undefined
   */
  getKollectiveToken = (): string | undefined => {
    const reporterState = this.state.reporter;
    return reporterState.sessionToken || reporterState.contentToken;
  };

  getContentToken(): string | undefined {
    return this.state.reporter.contentToken;
  }

  setContentToken(contentToken?: string, token?: string): string | undefined {
    const reporterState = this.state.reporter;
    if (contentToken || token) {
      reporterState.contentToken = contentToken || token;
    }
    return this.getKollectiveToken();
  }

  setSessionToken(sessionToken: string): void {
    const reporterState = this.state.reporter;
    if (sessionToken) {
      reporterState.sessionToken = sessionToken;
    } else {
      log.error(ErrorMessages.MISSING_KOLLECTIVE_TOKEN);
    }
  }

  private getToken(): string | undefined {
    const initToken = this.getInitToken();
    return this.getKollectiveToken() || initToken;
  }

  getInitHeaders(): Record<string, string> {
    const { play } = this.state;
    return getKollectiveHeaders({ ...play, token: this.getToken() });
  }

  // Overridden in MSFT plugins.
  // Used for JIT Publishing within the ksdk
  getInitToken(): string | undefined {
    return this.integratorToken;
  }
}
